import React from 'react';
import PropTypes from 'prop-types';
import Col from 'react-bootstrap/Col';

import MultiSelect from 'components/MultiSelect';
import {extractDynamicFieldOptions, getFieldNameKey} from './utils';

/**
 * DynamicFilter component - renders a single dynamic column filter
 * Used by CoverageFilters to render dynamic filters in the correct order
 * Supports fieldName formats:
 *   - Single field: "payer"
 *   - Pipe-delimited: "payer|pbmClient"
 */
export const DynamicFilter = ({
  column,
  coveragePositions,
  selectedDynamicFilters,
  onFilterChange,
  inProgress,
}) => {
  // Only render if dynamicColumns is enabled and coveragePositions are available
  if (!coveragePositions || !Array.isArray(coveragePositions) || coveragePositions.length === 0) {
    return null;
  }

  const fieldName = column.fieldName;
  const fieldNameKey = getFieldNameKey(fieldName);
  let dynamicOptions = extractDynamicFieldOptions(coveragePositions, fieldName);

  // Apply custom ordering if specified
  if (column.order && Array.isArray(column.order) && column.order.length > 0) {
    const normalize = (str) => String(str || '').trim().toLowerCase();
    const optionsMap = new Map(dynamicOptions.map((opt) => [normalize(opt.label), opt]));
    const addedIds = new Set();

    const ordered = column.order
      .map((label) => optionsMap.get(normalize(label)))
      .filter((opt) => {
        if (opt && !addedIds.has(opt.id)) {
          addedIds.add(opt.id);
          return true;
        }
        return false;
      });

    const remaining = dynamicOptions.filter((opt) => !addedIds.has(opt.id));
    dynamicOptions = [...ordered, ...remaining];
  }

  const selectedDynamicValues = selectedDynamicFilters?.[fieldNameKey] || [];

  // Create handleChange function for this specific dynamic filter
  const handleChange = (selected) => {
    onFilterChange({
      selectedDynamicFilters: {
        ...(selectedDynamicFilters || {}),
        [fieldNameKey]: selected
      }
    });
  };

  return (
    <Col key={`dynamic-${fieldNameKey}`} sm={column.gridWidth} className={`offset-sm-${column.offset}`}>
      <MultiSelect
        label={column.label}
        placeholder={column.placeholder}
        options={dynamicOptions}
        selected={selectedDynamicValues}
        isFetching={inProgress}
        handleChange={handleChange}
        selectHintOnEnter={false}
        type={`dynamic-${fieldNameKey}`}
      />
    </Col>
  );
};

DynamicFilter.propTypes = {
  column: PropTypes.shape({
    filter: PropTypes.string.isRequired,
    fieldName: PropTypes.string, // Optional: supports "payer" or "payer|pbmClient"
    gridWidth: PropTypes.number,
    offset: PropTypes.number,
    label: PropTypes.string,
    placeholder: PropTypes.string,
    order: PropTypes.array,
  }).isRequired,
  coveragePositions: PropTypes.array,
  selectedDynamicFilters: PropTypes.object,
  onFilterChange: PropTypes.func.isRequired,
  inProgress: PropTypes.bool.isRequired,
  settings: PropTypes.object.isRequired,
  styleSheet: PropTypes.object,
};

export default DynamicFilter;

